import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type TSelectedItem = { historyId: number; exerciseId: number };

type TExerciseSelectionContext = {
  selectedItems: TSelectedItem[];
  selectedIds: number[];
  selectedExerciseIds: number[];
  toggleItem: (historyId: number, exerciseId: number) => void;
  clearSelection: () => void;
};

const ExerciseSelectionContext = createContext<TExerciseSelectionContext>(
  {} as TExerciseSelectionContext
);

export const ExerciseSelectionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [selectedItems, setSelectedItems] = useState<TSelectedItem[]>([]);

  const toggleItem = useCallback((historyId: number, exerciseId: number) => {
    setSelectedItems((prev) =>
      prev.some((x) => x.historyId === historyId)
        ? prev.filter((x) => x.historyId !== historyId)
        : [...prev, { historyId, exerciseId }]
    );
  }, []);

  const clearSelection = useCallback(() => setSelectedItems([]), []);

  const selectedIds = useMemo(
    () => selectedItems.map((x) => x.historyId),
    [selectedItems]
  );

  const selectedExerciseIds = useMemo(
    () => selectedItems.map((x) => x.exerciseId),
    [selectedItems]
  );

  return (
    <ExerciseSelectionContext.Provider
      value={{
        selectedItems,
        selectedIds,
        selectedExerciseIds,
        toggleItem,
        clearSelection,
      }}
    >
      {children}
    </ExerciseSelectionContext.Provider>
  );
};

export const useExerciseSelection = () => useContext(ExerciseSelectionContext);
